import {EyeInvisibleOutlined, EyeTwoTone, SyncOutlined} from '@ant-design/icons';
import {scorePassword} from '@dofleini/validator';
import {Button, Input, Progress} from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Tooltip} from '@/components/Tooltip';
import {MIN_PASSWORD_SCORE_STRENGTH} from '@/constants';
import {generateStr} from '@/utils/passGenerator';

import style from './index.less';

const getStrength = (val) => {
  if (val < 20)
    return 'veryWeak';
  if (val <= MIN_PASSWORD_SCORE_STRENGTH)
    return 'weak';
  if (val < 70)
    return 'acceptable';
  if (val < 90)
    return 'good';

  return 'Excellent';
};

const externalValidations = {
  min8Characters: /\d{8}$/,
  paeVerification: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])([A-Za-z\d$@$!%*?&]|[^ ])/,
};

const Password = ({onChange, lateral, size, generate, ...props}) => {
  const {t} = useTranslation('common');

  const [score, setScore] = useState({score: 0, percent: 0});

  const handleChange = useCallback((e) => {
    const currentScore = scorePassword(e.target.value, externalValidations);
    const percent = Math.min(currentScore / MIN_PASSWORD_SCORE_STRENGTH * 100, 100);
    setScore({score: currentScore, percent});
    onChange && onChange(e);
  }, [onChange]);

  useEffect(() => {
    if (props.value) {
      const currentScore = scorePassword(props.value, externalValidations);
      const percent = Math.min(currentScore / MIN_PASSWORD_SCORE_STRENGTH * 100, 100);
      setScore({score: currentScore, percent});
    }
  }, [props.value]);

  const onGenerate = useCallback(() => {
    onChange && onChange(generateStr());
  }, [onChange]);

  const valid = useMemo(() => score?.score >= MIN_PASSWORD_SCORE_STRENGTH, [score]);

  const color = useMemo(() => !valid ? '#ff4d4f' : score?.score < MIN_PASSWORD_SCORE_STRENGTH ? '#faad14' : '#52c41a', [score, valid]);

  const strength = getStrength(score.score);

  return (
    <div className={'relative'}>
      <div className={classNames('flex', style.inputContainer)}>
        <Input.Password
          type="password"
          className={'rounded-r-none border-r-0'}
          required
          iconRender={visible => (visible ? <EyeTwoTone/> : <EyeInvisibleOutlined/>)}
          size={size}
          {...props}
          onChange={handleChange}
        />
        {!!generate && <Tooltip title={t('passwordGenerate')}>
          <Button onClick={onGenerate} className={'rounded-l-none'} icon={<SyncOutlined/>} size={size}/>
        </Tooltip>}
      </div>

      <div className={classNames(style.passMessage, {[style.lateral]: lateral}, {'hidden': !props.value})}>
        <span
          className={classNames({'text-error': !valid}, {'text-success': valid && score.score >= 40}, {'text-warning': valid && score.score < 40})}>
          {t('strength')}: {t(strength)}
        </span>
        <Progress
          percent={score.percent}
          size="small"
          strokeColor={color}
          status={valid ? 'success' : 'exception'}/>
        {t('passwordStrength')}
      </div>
    </div>
  );

};

export default memo(Password);

Password.propTypes = {
  lateral: PropTypes.bool,
  generate: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  size: PropTypes.any,
};

Password.defaultProps = {
  generate: true,
  lateral: true
};
